import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button, Space, Tag, Typography, Spin, Alert, Empty, Select, Input,
  Descriptions, Card, Steps, Statistic, Progress, Popover, Divider, Checkbox
} from 'antd';
import {
  ArrowLeftOutlined, ReloadOutlined, InfoCircleOutlined,
  ExpandAltOutlined, ShrinkOutlined
} from '@ant-design/icons';
import { ErpMasterShell } from '../../../../components/common/erp-grid';
import { proformaApi } from './proformaApi';
import useApprovalMfa from '../../../../hooks/useApprovalMfa';
import { notifySuccess, notifyError, parseApiError } from '../../../../utils/notify';

const { Text, Title } = Typography;

// ════════════════════════════════════════════════════════════════════
// Stage WO-3.3 — post-cut Change Order workbench (full page).
//
// A CONFIRMED, released order whose glass is already cut can't be edited in
// place. This page is STEP 1 of the change: pick a fate for each already-cut
// piece (Scrap · Bill · Off-cut), run the L1 Approval-MFA step-up, and open the
// new revision's editor to add the customer's new sizes. API contract unchanged.
// ════════════════════════════════════════════════════════════════════

const FATE_ORDER = ['scrap', 'bill', 'offcut'];
const FATE_LABEL = { scrap: 'Scrap', bill: 'Bill', offcut: 'Off-cut' };
const fateSelectOptions = FATE_ORDER.map((f) => ({ value: f, label: FATE_LABEL[f] }));

const STATUS_COLOR = { PENDING: 'gold', CONFIRMED: 'green', HOLD: 'orange', CANCELLED: 'red' };
const INR = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 });
const money = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : INR.format(Number(v)));

// Neutral proforma-style grid (matches the proforma line grid).
const COG_CSS = `
.cog-wrap{overflow:auto;border:1px solid #e5e9ef;border-radius:8px;background:#fff}
.cog{width:100%;border-collapse:collapse;font-size:12px;min-width:760px;color:#141b26}
.cog th{position:sticky;top:0;z-index:3;background:#f4f6f9;color:#5b6672;text-transform:uppercase;
  font-size:10px;letter-spacing:.04em;font-weight:700;padding:9px 10px;border-bottom:1px solid #d7dde6;
  border-right:1px solid #e5e9ef;text-align:left;white-space:nowrap}
.cog td{padding:6px 10px;border-bottom:1px solid #eef1f5;border-right:1px solid #eef1f5;vertical-align:middle}
.cog td:last-child,.cog th:last-child{border-right:none}
.cog tbody tr:hover td{background:#f6f9fd}
.cog tr.sel-on td{background:#eff6ff}
.cog tr.sel-on:hover td{background:#e6f0fe}
.cog .sel{width:40px;text-align:center}
.cog .num{text-align:right;font-variant-numeric:tabular-nums;color:#8895a6;width:46px}
.cog .center{text-align:center}
.cog .type{font-weight:600;color:#1f2733;white-space:nowrap}
.cog .desc{max-width:340px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cog .prog{font-variant-numeric:tabular-nums;color:#1677ff;font-weight:600}
.cog .caret{border:none;background:transparent;cursor:pointer;color:#8895a6;font-size:11px;width:16px;height:16px;
  border-radius:4px;padding:0;margin-right:4px;line-height:1}
.cog .caret:hover{background:#e6ebf1;color:#1e293b}
.cog tr.det td{background:#f8fafc;border-bottom:1px solid #eef1f5;padding:8px 10px 10px}
.cog .det-box{display:flex;flex-wrap:wrap;gap:8px 26px;font-size:12px;color:#334155}
.cog .det-box .k{font-family:ui-monospace,Menlo,Consolas,monospace;text-transform:uppercase;font-size:9.5px;
  color:#8895a6;font-weight:700;margin-right:7px;letter-spacing:.03em}
.cog .det-box .v{color:#1f2733}
`;

function fabText(fab) {
  if (!fab) return '';
  const entries = Array.isArray(fab) ? fab : (Array.isArray(fab.entries) ? fab.entries : []);
  return entries
    .filter((e) => e && Number(e.count) > 0)
    .map((e) => `${e.label || e.code || e.name || '?'} ×${Number(e.count)}`)
    .join(', ');
}

function rowSpec(p) {
  const s = (p && p.spec) || {};
  const w = s.widthMm != null ? s.widthMm : (s.actual_width_mm ?? null);
  const h = s.heightMm != null ? s.heightMm : (s.actual_height_mm ?? null);
  // Assembly (LAMI/DGU) cut piece = one GLASS LAYER. A post-cut change happens
  // before lamination, so each layer is disposed on its own — show THIS layer's
  // spec (from the pane snapshot), not the whole laminate.
  if (p && p.isAssembly && p.paneSpec) {
    const ps = p.paneSpec;
    return {
      type: `Layer ${p.paneIndex}`,
      desc: ps.itemDesc || ps.item_desc || s.glassTypeCode || s.glass_type_code || '—',
      size: w && h ? `${w} × ${h}` : '—',
      qty: 1
    };
  }
  return {
    type: s.lineType || s.line_type || s.glassTypeCode || s.glass_type_code || '—',
    desc: s.itemDesc || s.item_desc || s.glassTypeCode || s.glass_type_code || '—',
    size: w && h ? `${w} × ${h}` : '—',
    qty: s.qty != null ? s.qty : (s.quantity != null ? s.quantity : '—')
  };
}

function customerName(h) {
  const b = (h && h.billToSnapshot) || {};
  return b.customerName || b.name || b.legalName || b.tradeName || '—';
}

const FATE_HELP = (
  <div style={{ maxWidth: 340, fontSize: 13, lineHeight: 1.55 }}>
    <p style={{ margin: '0 0 9px' }}><b>Scrap</b> — discard the cut piece. Charged <b>₹0</b>.</p>
    <p style={{ margin: '0 0 9px' }}><b>Bill</b> — keep it: the piece is finished and delivered as-is, charged at the
      original line price. Not a discount for its stage — adjust the price later in the revision if that isn&apos;t fair.</p>
    <p style={{ margin: 0 }}><b>Off-cut</b> — scrap it, but return the usable glass to stock. Charged <b>₹0</b>.</p>
  </div>
);

export default function ChangeOrderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { runWithApproval, ApprovalMfaModalSlot } = useApprovalMfa();

  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [header, setHeader] = useState(null);
  const [workOrder, setWorkOrder] = useState(null);
  const [cutPieces, setCutPieces] = useState([]);
  const [fates, setFates] = useState({});
  const [reason, setReason] = useState('');
  const [openRows, setOpenRows] = useState(() => new Set());
  const [selected, setSelected] = useState(() => new Set());   // rows ticked for bulk fate
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true); setLoadError(null); setFates({}); setOpenRows(new Set()); setSelected(new Set());
    Promise.all([
      proformaApi.get(id).catch(() => null),
      proformaApi.changeOrderCutPieces(id)
    ])
      .then(([hdr, co]) => {
        setHeader(hdr || null);
        setWorkOrder((co && co.workOrder) || null);
        setCutPieces((co && co.cutPieces) || []);
      })
      .catch((err) => setLoadError(parseApiError(err)?.description || 'Could not load the change-order workbench.'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const toggleRow = (pieceId) => setOpenRows((prev) => {
    const next = new Set(prev);
    if (next.has(pieceId)) next.delete(pieceId); else next.add(pieceId);
    return next;
  });
  const setOne = (pieceId, fate) => setFates((f) => ({ ...f, [pieceId]: fate }));
  const expandAll = () => setOpenRows(new Set(cutPieces.map((p) => p.pieceId)));
  const collapseAll = () => setOpenRows(new Set());

  // ── Row selection + bulk fate ──
  const toggleOne = (pieceId, checked) => setSelected((prev) => {
    const next = new Set(prev);
    if (checked) next.add(pieceId); else next.delete(pieceId);
    return next;
  });
  const toggleAll = (checked) => setSelected(checked ? new Set(cutPieces.map((p) => p.pieceId)) : new Set());
  const clearSelection = () => setSelected(new Set());
  const applyToSelected = (fate) => {
    if (!selected.size) return;
    setFates((f) => {
      const next = { ...f };
      selected.forEach((pid) => { next[pid] = fate; });
      return next;
    });
  };

  const total = cutPieces.length;
  const cutByLine = useMemo(() => {
    const m = {};
    cutPieces.forEach((p) => { const k = p.lineNo; m[k] = (m[k] || 0) + 1; });
    return m;
  }, [cutPieces]);
  const tally = useMemo(() => {
    const t = { scrap: 0, bill: 0, offcut: 0 };
    cutPieces.forEach((p) => { const f = fates[p.pieceId]; if (f && t[f] != null) t[f] += 1; });
    return t;
  }, [cutPieces, fates]);
  const decided = tally.scrap + tally.bill + tally.offcut;
  const remaining = total - decided;
  const allDecided = total > 0 && remaining === 0;
  const pct = total ? Math.round((decided / total) * 100) : 0;
  const selectedCount = selected.size;
  const allSelected = total > 0 && selectedCount === total;
  const someSelected = selectedCount > 0 && selectedCount < total;

  const proformaNo = header && header.proformaNo;
  const woNo = (workOrder && workOrder.woNo) || (header && header.workOrder && header.workOrder.woNo) || null;

  async function apply() {
    if (!allDecided) return;
    const dispositions = cutPieces.map((p) => ({ pieceId: p.pieceId, fate: fates[p.pieceId] }));
    setApplying(true);
    try {
      const result = await runWithApproval({
        operationKey: 'production.workorder.change_order',
        resourceType: 'proforma',
        resourceId: id,
        operationLabel: 'Raise Change Order',
        resourceLabel: proformaNo,
        action: (challengeId) => proformaApi.changeOrder(id, {
          dispositions, reason: reason || null, approvalMfaChallengeId: challengeId
        })
      });
      notifySuccess('Change Order applied — new revision created.');
      navigate(`/erp/sales/proformas/${(result && result.id) || id}`);
    } catch (err) {
      if (err && err.name === 'ApprovalMfaCancelled') return;   // user closed the MFA modal
      // Pass the REAL axios error (not a title string) as notifyError's first arg —
      // otherwise parseApiError runs on a string, finds no `.response`, and returns
      // NETWORK_ERROR ("Unable to connect"), masking a genuine 409. Supply the
      // backend envelope message as the fallback description so an unmapped domain
      // code (fate/revision conflict, etc.) shows its real reason.
      const be = err && err.response && err.response.data && err.response.data.error;
      notifyError(err, {
        title: 'Could not apply the Change Order',
        description: (be && be.message) || 'Please review the pieces and try again.'
      });
    } finally {
      setApplying(false);
    }
  }

  const notReleased = !loading && !workOrder;
  const nothingCut = !loading && workOrder && total === 0;
  const showGrid = !loading && total > 0;

  return (
    <ErpMasterShell>
      <style>{COG_CSS}</style>
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>

        {/* Top bar */}
        <div className="flex items-center justify-between mb-4">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/erp/sales/proformas')}>Proformas</Button>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
        </div>

        {/* Process rail — where this page sits in the change. */}
        <Steps
          size="small"
          current={0}
          responsive
          className="mb-4"
          items={[
            { title: 'Decide fates', description: 'Scrap · Bill · Off-cut per cut piece' },
            { title: 'Add new sizes', description: 'In the new revision editor' },
            { title: 'Release', description: 'Append pieces to the Work Order' }
          ]}
        />

        {loadError ? (
          <Alert type="error" showIcon message={loadError} className="mb-4"
            action={<Button size="small" onClick={load}>Retry</Button>} />
        ) : null}

        {loading ? (
          <div style={{ padding: 64, textAlign: 'center' }}><Spin size="large" /></div>
        ) : null}

        {/* Header card — identity + one-line context + live stat strip. */}
        {header && !loading ? (
          <Card
            className="mb-4"
            style={{ borderLeft: '3px solid #faad14' }}
            styles={{ body: { padding: 20 } }}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <Title level={4} style={{ margin: '0 0 6px' }}>
                  <Space size={8} wrap>
                    <span>Raise Change Order</span>
                    {proformaNo ? <Tag color="gold" style={{ marginInlineEnd: 0 }}>{proformaNo}</Tag> : null}
                    {woNo ? <Tag color="blue" style={{ marginInlineEnd: 0 }}>WO {woNo}</Tag> : null}
                    {header.status ? <Tag color={STATUS_COLOR[header.status] || 'default'} style={{ marginInlineEnd: 0 }}>{header.status}</Tag> : null}
                  </Space>
                </Title>
                <Text type="secondary" style={{ fontSize: 13, maxWidth: 560, display: 'inline-block' }}>
                  Cut glass can&apos;t be un-cut. Choose what happens to each already-cut piece — the affected line is
                  struck and a new revision opens for the customer&apos;s new sizes.
                </Text>
              </div>

              <div className="flex items-center" style={{ gap: 0 }}>
                <Statistic title="In production" value={total} suffix="pcs" valueStyle={{ fontSize: 22 }} />
                <Divider type="vertical" style={{ height: 44, margin: '0 20px' }} />
                <div style={{ minWidth: 132 }}>
                  <div style={{ color: '#8c8c8c', fontSize: 14, marginBottom: 2 }}>Decided</div>
                  <div style={{ fontSize: 22, lineHeight: 1.1, color: allDecided ? '#389e0d' : '#141b26' }}>
                    {decided}<span style={{ color: '#8c8c8c', fontSize: 15 }}> / {total}</span>
                  </div>
                  <Progress percent={pct} size="small" showInfo={false}
                    strokeColor={allDecided ? '#52c41a' : '#faad14'} style={{ margin: '4px 0 0' }} />
                </div>
                <Divider type="vertical" style={{ height: 44, margin: '0 20px' }} />
                <Statistic title="Order value" value={money(header.grandTotal)} valueStyle={{ fontSize: 22 }} />
              </div>
            </div>
          </Card>
        ) : null}

        {/* Order details */}
        {header && !loading ? (
          <Card size="small" title="Order details" className="mb-4">
            <Descriptions bordered size="small" column={{ xs: 1, sm: 2, lg: 3 }}
              labelStyle={{ width: 138, fontWeight: 600, background: '#fafbfc' }}>
              <Descriptions.Item label="Customer">{customerName(header)}</Descriptions.Item>
              <Descriptions.Item label="Proforma">{proformaNo || '—'}</Descriptions.Item>
              <Descriptions.Item label="Proforma date">{header.proformaDate || '—'}</Descriptions.Item>
              <Descriptions.Item label="Work Order">{woNo || '—'}</Descriptions.Item>
              <Descriptions.Item label="Revision">{header.revisionNo != null ? `Rev ${header.revisionNo}` : 'Rev 1'}</Descriptions.Item>
              <Descriptions.Item label="Grand total">{money(header.grandTotal)}</Descriptions.Item>
              {header.projectName ? <Descriptions.Item label="Project">{header.projectName}</Descriptions.Item> : null}
              {header.poNo ? <Descriptions.Item label="PO No">{header.poNo}</Descriptions.Item> : null}
              <Descriptions.Item label="Cut pieces">{total}</Descriptions.Item>
            </Descriptions>
          </Card>
        ) : null}

        {/* States */}
        {notReleased ? (
          <Alert type="warning" showIcon className="mb-4"
            message="This order is not released to production — there is nothing to change here." />
        ) : null}
        {nothingCut ? (
          <Card className="mb-4">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No cut pieces on this order yet — use Recall for a clean pre-cut change.">
              <Button onClick={() => navigate('/erp/sales/proformas')}>Back to proformas</Button>
            </Empty>
          </Card>
        ) : null}

        {/* Decision card */}
        {showGrid ? (
          <Card
            size="small"
            className="mb-4"
            title={<span>Already-cut pieces <Text type="secondary" style={{ fontWeight: 400, fontSize: 12 }}>· pick a fate for each</Text></span>}
            extra={
              <Space size={4}>
                <Button size="small" type="text" icon={<ExpandAltOutlined />} onClick={expandAll}>Expand</Button>
                <Button size="small" type="text" icon={<ShrinkOutlined />} onClick={collapseAll}>Collapse</Button>
                <Popover content={FATE_HELP} title="What each fate does" trigger="click" placement="bottomRight">
                  <Button size="small" type="text" icon={<InfoCircleOutlined />}>What do these mean?</Button>
                </Popover>
              </Space>
            }
          >
            {/* Bulk toolbar — tick rows, then apply one fate to all of them. */}
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <Text type="secondary" style={{ fontSize: 12.5 }}>
                {selectedCount
                  ? <span><b>{selectedCount}</b> selected</span>
                  : 'Tick rows to set several at once, or use each row’s dropdown.'}
              </Text>
              <Space size={8}>
                <Text type="secondary" style={{ fontSize: 12.5 }}>Apply to selected:</Text>
                <Select
                  size="small"
                  style={{ width: 150 }}
                  placeholder="Choose fate…"
                  value={null}
                  disabled={!selectedCount}
                  options={fateSelectOptions}
                  onChange={applyToSelected}
                />
                {selectedCount ? <Button size="small" type="text" onClick={clearSelection}>Clear</Button> : null}
              </Space>
            </div>

            <div className="cog-wrap">
              <table className="cog">
                <thead>
                  <tr>
                    <th className="sel">
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={(e) => toggleAll(e.target.checked)}
                        aria-label="Select all"
                      />
                    </th>
                    <th className="num">#</th>
                    <th style={{ width: 96 }}>Type</th>
                    <th>Item description</th>
                    <th className="center" style={{ width: 116 }}>W × H (mm)</th>
                    <th className="center" style={{ width: 78 }}>Order qty</th>
                    <th className="center" style={{ width: 92 }}>In production</th>
                    <th className="center" style={{ width: 150 }}>Fate</th>
                  </tr>
                </thead>
                <tbody>
                  {cutPieces.map((p, idx) => {
                    const r = rowSpec(p);
                    const isOpen = openRows.has(p.pieceId);
                    const isSel = selected.has(p.pieceId);
                    const fabs = fabText(p.fabrication);
                    const rems = Array.isArray(p.remarks) ? p.remarks.join(' · ') : '';
                    return (
                      <Fragment key={p.pieceId}>
                        <tr className={isSel ? 'sel-on' : ''}>
                          <td className="sel">
                            <Checkbox checked={isSel} onChange={(e) => toggleOne(p.pieceId, e.target.checked)}
                              aria-label={`Select piece ${idx + 1}`} />
                          </td>
                          <td className="num">
                            <button type="button" className="caret" aria-label={isOpen ? 'Collapse' : 'Expand'}
                              onClick={() => toggleRow(p.pieceId)}>{isOpen ? '▾' : '▸'}</button>
                            {idx + 1}
                          </td>
                          <td className="type">{r.type}</td>
                          <td className="desc" title={r.desc}>{r.desc}</td>
                          <td className="center">{r.size}</td>
                          <td className="center">{r.qty}</td>
                          <td className="center prog">{cutByLine[p.lineNo] || 1}</td>
                          <td className="center">
                            <Select
                              size="small"
                              style={{ width: 132 }}
                              placeholder="Select…"
                              value={fates[p.pieceId]}
                              options={fateSelectOptions}
                              onChange={(val) => setOne(p.pieceId, val)}
                            />
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="det">
                            <td colSpan={2} />
                            <td colSpan={6}>
                              <div className="det-box">
                                <span><span className="k">Stage</span><span className="v">{p.currentStage || '—'}</span></span>
                                <span><span className="k">Barcode</span><Text code style={{ fontSize: 11.5 }}>{p.barcode}</Text></span>
                                <span><span className="k">Fabrication</span><span className="v">{fabs || '—'}</span></span>
                                <span><span className="k">Remarks</span><span className="v">{rems || '—'}</span></span>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 16, maxWidth: 620 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Reason for the change (optional)</Text>
              <Input.TextArea
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. customer changed the height after cutting started"
                maxLength={500}
                showCount
                style={{ marginTop: 4 }}
              />
            </div>
          </Card>
        ) : null}

        {/* Sticky action bar */}
        {showGrid ? (
          <div className="flex items-center justify-between gap-3"
            style={{
              position: 'sticky', bottom: 0, zIndex: 5, background: '#fff',
              border: '1px solid #eceff3', borderRadius: 10, padding: '12px 16px',
              boxShadow: '0 -4px 16px rgba(20,27,38,.06)'
            }}>
            <div className="flex items-center gap-3 flex-wrap">
              {allDecided
                ? <Text style={{ fontSize: 13 }}>All {total} pieces decided — ready to apply.</Text>
                : <Text type="secondary" style={{ fontSize: 13 }}>{decided} of {total} decided · <b style={{ color: '#d48806' }}>{remaining} left</b></Text>}
              {decided > 0 ? (
                <Space size={4}>
                  <Tag style={{ marginInlineEnd: 0 }}>Bill {tally.bill}</Tag>
                  <Tag style={{ marginInlineEnd: 0 }}>Scrap {tally.scrap}</Tag>
                  <Tag style={{ marginInlineEnd: 0 }}>Off-cut {tally.offcut}</Tag>
                </Space>
              ) : null}
            </div>
            <Space>
              <Button onClick={() => navigate('/erp/sales/proformas')} disabled={applying}>Cancel</Button>
              <Button type="primary" danger onClick={apply} loading={applying} disabled={!allDecided || applying}>
                Apply Change Order
              </Button>
            </Space>
          </div>
        ) : null}
      </div>
      {ApprovalMfaModalSlot}
    </ErpMasterShell>
  );
}
